import {type SetState} from '@wandb/weave/common/util/types';
import {useCallback, useEffect, useLayoutEffect, useState} from 'react';

/**
 * Paginated rows together with the page controls that act on them.
 * Tables consume this directly; tabs/hooks produce it.
 *
 * `allRows` holds every row currently buffered (often spanning multiple
 * pages), while `pageRows` is the slice for the current display page.
 */
export type PageController<TRow> = {
  allRows: TRow[];
  pageRows: TRow[];
  loading: boolean;
  error: Error | undefined;
  page: number;
  pageSize: number;
  setPage: SetState<number>;
  changePageSize: (pageSize: number) => void;
  hasNextPage: boolean;
  fetchingNextPage: boolean;
  fetchNextPage: () => void;
};

/**
 * Slice rows down to the current display page. Clamps `page` against the
 * available rows so a stale page index outrunning the data renders the
 * last page rather than an empty one.
 */
function sliceToPage<TRow>(
  rows: TRow[],
  pageIdx: number,
  pageSize: number
): TRow[] {
  const numPages = Math.ceil(rows.length / Math.max(pageSize, 1));
  const maxPageIdx = Math.max(0, numPages - 1);
  const pageStartIdx = Math.min(pageIdx, maxPageIdx);
  const rowStartIdx = pageStartIdx * pageSize;
  return rows.slice(rowStartIdx, rowStartIdx + pageSize);
}

/**
 * Minimum shape a cursor-paginated data source must expose for
 * `useBufferedPageController` to drive page gating and fetch-ahead.
 */
export type PaginatedRowSource<TRow> = {
  rows: TRow[];
  loading: boolean;
  error: Error | undefined;
  hasNextPage: boolean;
  fetchingNextPage: boolean;
  fetchNextPage: () => void;
};

type UseBufferedPageControllerParams<TRow> = {
  source: PaginatedRowSource<TRow>;
  requestPage: number;
  pageSize: number;
  setRequestPage: SetState<number>;
  setPageSize: SetState<number>;
  resetKey?: unknown;
};

function hasEnoughRows(numRows: number, pageIdx: number, pageSize: number) {
  return numRows >= (pageIdx + 1) * pageSize;
}

/**
 * Coordinates "request page" (what the client asked for) with "display page"
 * (what we're actually rendering) so that we keep showing the current page
 * while the next one loads, and flip over only once enough rows are in hand.
 *
 * Also auto-fetches the next backend page whenever the requested page would
 * exceed the rows we currently have, so callers don't have to drive it.
 */
export function useBufferedPageController<TRow>({
  source,
  requestPage,
  pageSize,
  setRequestPage,
  setPageSize,
  resetKey,
}: UseBufferedPageControllerParams<TRow>): PageController<TRow> {
  const [displayPage, setDisplayPage] = useState(0);

  // Reset both pages together when the backing dataset changes, such as when
  // a new server-side search or scope switch replaces the row set.
  useLayoutEffect(() => {
    setRequestPage(0);
    setDisplayPage(0);
  }, [resetKey, setRequestPage]);

  const pageReady =
    hasEnoughRows(source.rows.length, requestPage, pageSize) ||
    (!source.hasNextPage && !source.loading && !source.fetchingNextPage);

  // Auto-fetch the next backend page when the requested page extends beyond
  // the rows we've already loaded. Self-guarded inside source.fetchNextPage.
  useEffect(() => {
    if (source.loading || source.fetchingNextPage) {
      return;
    }
    if (
      !hasEnoughRows(source.rows.length, requestPage, pageSize) &&
      source.hasNextPage
    ) {
      source.fetchNextPage();
    }
    // hasEnoughRows is derived from source.rows.length + the args, so
    // tracking those primitives is sufficient.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    source.loading,
    source.fetchingNextPage,
    source.hasNextPage,
    source.rows.length,
    source.fetchNextPage,
    requestPage,
    pageSize,
  ]);

  // Once the requested page is backed by loaded rows, reveal it.
  useEffect(() => {
    if (pageReady && displayPage !== requestPage) {
      setDisplayPage(requestPage);
    }
  }, [displayPage, pageReady, requestPage]);

  const setPage: SetState<number> = useCallback(
    next => {
      const nextPage = Math.max(
        0,
        typeof next === 'function' ? next(requestPage) : next
      );
      setRequestPage(nextPage);

      // Jumping back or landing on an already-loaded page is safe to reveal
      // immediately; otherwise wait for the auto-fetch + sync effect above.
      const alreadyBacked =
        hasEnoughRows(source.rows.length, nextPage, pageSize) ||
        !source.hasNextPage;
      if (nextPage <= displayPage || alreadyBacked) {
        setDisplayPage(nextPage);
      }
    },
    [
      displayPage,
      pageSize,
      requestPage,
      setRequestPage,
      source.hasNextPage,
      source.rows.length,
    ]
  );

  const changePageSize = useCallback(
    (nextPageSize: number) => {
      setPageSize(nextPageSize);
      setRequestPage(0);
      setDisplayPage(0);
    },
    [setPageSize, setRequestPage]
  );

  // While the initial page (or a fresh page after resize) is still loading,
  // present an empty loading state instead of stale rows.
  const pendingFirstPage = requestPage === displayPage && !pageReady;

  const allRows = pendingFirstPage ? [] : source.rows;
  return {
    allRows,
    pageRows: sliceToPage(allRows, displayPage, pageSize),
    loading: pendingFirstPage || (source.loading && !source.fetchingNextPage),
    error: source.error,
    page: displayPage,
    pageSize,
    setPage,
    changePageSize,
    hasNextPage: pendingFirstPage ? false : source.hasNextPage,
    fetchingNextPage: pendingFirstPage ? false : source.fetchingNextPage,
    fetchNextPage: source.fetchNextPage,
  };
}
